import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api, { formatIDR, formatDate } from "@/lib/api";
import {
  FileText,
  Send,
  AlarmClock,
  AlertTriangle,
  RefreshCw,
  Loader2,
  CheckCircle2,
  XCircle,
  Activity,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";

const StatusBadge = ({ status }) => {
  const map = {
    SENT: "bg-[#D1FAE5] text-[#059669]",
    PENDING: "bg-[#FEF3C7] text-[#B45309]",
    FAILED: "bg-[#FEE2E2] text-[#DC2626]",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || "bg-[#F1F5F9] text-[#475569]"}`}>
      {status}
    </span>
  );
};

const KpiCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="border border-[#E2E8F0] rounded-lg bg-white p-5" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div className="text-xs font-bold tracking-[0.05em] uppercase text-[#64748B]">{label}</div>
      <div className={`w-8 h-8 rounded-md flex items-center justify-center ${accent}`}>
        <Icon className="w-4 h-4" strokeWidth={1.8} />
      </div>
    </div>
    <div className="mt-3 text-3xl font-semibold tracking-tight text-[#0F172A] tabular-nums">{value}</div>
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      const { data } = await api.get("/dashboard/summary");
      if (alive) setData(data);
    })();
    return () => { alive = false; };
  }, [reloadKey]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/sync/now");
      toast.success(`Sync selesai: ${data.invoices_synced} invoice, ${data.customers_synced} pelanggan`);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error("Sync gagal: " + (e.response?.data?.detail || e.message));
    } finally {
      setSyncing(false);
    }
  };

  if (!data) {
    return (
      <div className="p-10 flex items-center gap-2 text-sm text-[#64748B]">
        <Loader2 className="w-4 h-4 animate-spin" /> Memuat dashboard...
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="dashboard-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-[#0F172A]">Dashboard</h1>
          <p className="text-sm text-[#64748B] mt-1">
            Pantau invoice, reminder otomatis & pengiriman WhatsApp dari satu tempat.
          </p>
          {data.kpis.deep_sync_pending > 0 && (() => {
            const localHour = new Date().getHours();
            const isActive = localHour >= 1 && localHour < 4;
            return isActive ? (
              <div className="mt-3 flex items-center gap-2 text-sm text-[#B45309] bg-[#FEF3C7] px-3 py-1.5 rounded-md border border-[#FDE68A] w-fit font-medium">
                <Loader2 className="w-4 h-4 animate-spin" />
                Sedang mengambil detail tagihan dari Accurate ({data.kpis.deep_sync_pending} sisa)
              </div>
            ) : (
              <div className="mt-3 flex items-center gap-2 text-sm text-[#64748B] bg-[#F1F5F9] px-3 py-1.5 rounded-md border border-[#E2E8F0] w-fit font-medium">
                🕐 {data.kpis.deep_sync_pending} tagihan menunggu pembaruan detail (otomatis diproses jam 01:00 - 04:00 pagi)
              </div>
            );
          })()}
        </div>
        <button
          onClick={handleSync}
          disabled={syncing}
          data-testid="sync-trigger-button"
          className="bg-[#0066FF] text-white hover:bg-[#0052CC] rounded-md px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-60"
        >
          {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Sync dari Accurate.id
        </button>
      </div>

      {data.daily_health && (
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-md ${data.daily_health.sync_failed === 0 && data.daily_health.messages_failed === 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              <Activity className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold text-sm text-[#0F172A]">Daily Health Report (Hari Ini)</h3>
              <p className="text-xs text-[#64748B]">Pemantauan otomatis kondisi sinkronisasi dan pengiriman pesan.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <div className="flex flex-col">
              <span className="text-[#64748B] text-xs">Sync Berhasil</span>
              <span className="font-medium text-[#0F172A]">{data.daily_health.sync_success} kali</span>
            </div>
            <div className="flex flex-col">
              <span className="text-[#64748B] text-xs">Sync Gagal</span>
              <span className={`font-medium ${data.daily_health.sync_failed > 0 ? 'text-red-600' : 'text-[#0F172A]'}`}>{data.daily_health.sync_failed} kali</span>
            </div>
            <div className="flex flex-col">
              <span className="text-[#64748B] text-xs">Orphan Invoice (Dihapus)</span>
              <span className="font-medium text-[#0F172A]">{data.daily_health.invoices_deleted} tagihan</span>
            </div>
            <div className="flex flex-col">
              <span className="text-[#64748B] text-xs">Kirim Pesan Gagal</span>
              <span className={`font-medium ${data.daily_health.messages_failed > 0 ? 'text-red-600' : 'text-[#0F172A]'}`}>{data.daily_health.messages_failed} pesan</span>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KpiCard
          icon={FileText}
          label="Total Invoice"
          value={data.kpis.total_invoices}
          accent="bg-[#EFF6FF] text-[#0066FF]"
          testid="kpi-total-invoices"
        />
        <KpiCard
          icon={Send}
          label="Pesan Terkirim Hari Ini"
          value={data.kpis.messages_sent_today}
          accent="bg-[#D1FAE5] text-[#059669]"
          testid="kpi-messages-sent-today"
        />
        <KpiCard
          icon={AlarmClock}
          label="Pending Reminder"
          value={data.kpis.pending_reminders}
          accent="bg-[#FEF3C7] text-[#B45309]"
          testid="kpi-pending-reminders"
        />
        <KpiCard
          icon={AlertTriangle}
          label="Overdue"
          value={data.kpis.overdue_alerts}
          accent="bg-[#FEE2E2] text-[#DC2626]"
          testid="kpi-overdue"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <div className="lg:col-span-2 border border-[#E2E8F0] rounded-lg bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs font-bold tracking-[0.05em] uppercase text-[#64748B]">7 Hari Terakhir</div>
              <div className="text-lg font-medium text-[#0F172A] mt-1">Aktivitas Pesan WhatsApp</div>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.chart} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" stroke="#64748B" fontSize={12} />
                <YAxis stroke="#64748B" fontSize={12} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 6, fontSize: 12, border: "1px solid #E2E8F0" }} />
                <Line type="monotone" dataKey="sent" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} name="Terkirim" />
                <Line type="monotone" dataKey="failed" stroke="#DC2626" strokeWidth={2} dot={{ r: 3 }} name="Gagal" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border border-[#E2E8F0] rounded-lg bg-white p-6">
          <div className="text-xs font-bold tracking-[0.05em] uppercase text-[#64748B]">Status</div>
          <div className="text-lg font-medium text-[#0F172A] mt-1 mb-4">Ringkasan Operasi</div>
                    <ul className="space-y-3 text-sm">
            <li className="flex items-center justify-between">
              <span className="text-[#475569]">Belum dikirim WA</span>
              <span className="font-medium text-[#0F172A] tabular-nums">{data.kpis.pending_reminders}</span>
            </li>
            <li className="flex items-center justify-between">
              <Link to="/invoices?filter=overdue" className="text-[#0066FF] hover:underline">Overdue</Link>
              <span className="font-medium text-[#0F172A] tabular-nums">{data.kpis.overdue_alerts}</span>
            </li>
            <li className="flex items-center justify-between">
              <Link to="/invoices?filter=h3" className="text-[#0066FF] hover:underline">Jatuh tempo H-3</Link>
              <span className="font-medium text-[#0F172A] tabular-nums">{data.kpis.upcoming_3d}</span>
            </li>
            <li className="flex items-center justify-between">
              <Link to="/invoices?filter=h7" className="text-[#0066FF] hover:underline">Jatuh tempo H-7</Link>
              <span className="font-medium text-[#0F172A] tabular-nums">{data.kpis.upcoming_7d}</span>
            </li>
            <li className="flex items-center justify-between">
              <Link to="/invoices?filter=h14" className="text-[#0066FF] hover:underline">Jatuh tempo H-14</Link>
              <span className="font-medium text-[#0F172A] tabular-nums">{data.kpis.upcoming_14d}</span>
            </li>
            <li className="flex items-center justify-between">
              <Link to="/invoices?filter=h15" className="text-[#0066FF] hover:underline">Jatuh tempo H-15</Link>
              <span className="font-medium text-[#0F172A] tabular-nums">{data.kpis.upcoming_15d}</span>
            </li>
            <li className="flex items-center justify-between">
              <Link to="/invoices?filter=h30" className="text-[#0066FF] hover:underline">Jatuh tempo H-30</Link>
              <span className="font-medium text-[#0F172A] tabular-nums">{data.kpis.upcoming_30d}</span>
            </li>
            <li className="flex items-center justify-between">
              <Link to="/invoices?filter=h45" className="text-[#0066FF] hover:underline">Jatuh tempo H-45</Link>
              <span className="font-medium text-[#0F172A] tabular-nums">{data.kpis.upcoming_45d}</span>
            </li>
            <li className="flex items-center justify-between">
              <span className="text-[#475569]">Mode integrasi</span>
              <span className="font-medium text-[#0F172A]">{data.kpis.mock_mode ? "MOCK" : "PANCAKE API"}</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border border-[#E2E8F0] rounded-lg bg-white overflow-hidden">
        <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
          <div className="text-sm font-medium text-[#0F172A]">Aktivitas Pesan Terbaru</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[#F8F9FA]">
            <tr className="text-xs font-medium text-[#64748B] uppercase tracking-wider">
              <th className="px-6 py-3 text-left">Waktu</th>
              <th className="px-6 py-3 text-left">Pelanggan</th>
              <th className="px-6 py-3 text-left">Invoice</th>
              <th className="px-6 py-3 text-left">Template</th>
              <th className="px-6 py-3 text-left">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E2E8F0]" data-testid="recent-logs-table">
            {data.recent_logs.length === 0 && (
              <tr>
                <td colSpan="5" className="px-6 py-8 text-center text-sm text-[#64748B]">
                  Belum ada aktivitas. Kirim invoice pertama dari halaman Invoices.
                </td>
              </tr>
            )}
            {data.recent_logs.map((log) => (
              <tr key={log.id} className="hover:bg-[#F8F9FA]">
                <td className="px-6 py-3 text-[#475569]">{formatDate(log.sent_at)}</td>
                <td className="px-6 py-3 text-[#0F172A] font-medium">{log.customer_name}</td>
                <td className="px-6 py-3 font-mono text-xs text-[#475569]">{log.invoice_number}</td>
                <td className="px-6 py-3 text-[#475569]">{log.template_key}</td>
                <td className="px-6 py-3">
                  <span className="inline-flex items-center gap-1">
                    {log.status === "SENT" ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#059669]" />
                    ) : (
                      <XCircle className="w-3.5 h-3.5 text-[#DC2626]" />
                    )}
                    <StatusBadge status={log.status} />
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
